// Super Admin → Financials → Refunds, disputes & review.
//
// Oct 3 2026 (Randy, the refund project). This file used to be 733 lines of
// four equal-weight cards, all expanded on arrival, with a voucher-only refund
// box at the bottom that answered a tip's charge id with a 404. It is now a
// composer:
//
//   1. Safe refund       — one identifier, any charge type, Connect flags
//                          set by the server. The top of the page, because it
//                          is the thing that stops money being lost.
//   2. Review queue      — banded by what it asks of you, one entry per
//                          incident, dollars and sentences instead of ids.
//   3. Dispute playbook  — collapsed unless a dispute is actually running.
//   4. Replay queue      — one line when empty, which is almost always.
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowsClockwise, CaretDown, CaretRight, Spinner,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { deadlineInfo, DEADLINE_STYLE, groupIncidents, REVIEW_MUTATED } from "@/lib/financialReview";
import FinancialReviewQueue from "@/components/FinancialReviewQueue";
import SafeRefundTool from "@/components/SafeRefundTool";

export default function VoucherRefundsPanel() {
  const [review, setReview] = useState(null);
  const [replays, setReplays] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const refundRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rv, rp, al] = await Promise.all([
        api.get(`/admin/vouchers/review?status=${showResolved ? "all" : "open"}&limit=100`),
        api.get("/admin/vouchers/replays?status=all&limit=50"),
        api.get("/admin/financial-review/alerts"),
      ]);
      setReview(rv.data);
      setReplays(rp.data);
      setAlerts(al.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load the review queue.");
    } finally { setLoading(false); }
  }, [showResolved]);
  useEffect(() => { load(); }, [load]);

  // Every mutation path (resolve, acknowledge, refund) already calls this, so
  // the banner and the nav badge get told in one place.
  const refresh = useCallback(async () => {
    await load();
    window.dispatchEvent(new Event(REVIEW_MUTATED));
  }, [load]);

  const refundCharge = (chargeId) => {
    setPrefill({ value: chargeId, nonce: Date.now() });
    refundRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <section data-testid="financials-voucher-refunds" id="financials-voucher-refunds">
      <div className="flex items-baseline gap-3 mb-4 pb-2 border-b hairline">
        <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Refunds, disputes &amp; review</h2>
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">
          one box · any payment
        </span>
        <button
          type="button" onClick={load}
          data-testid="voucher-review-refresh"
          className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded border border-[#D8D6D2] text-[#6B6A68] hover:border-[#A89360] hover:text-[#1C1B1A]"
        >
          <ArrowsClockwise size={11} weight="bold" />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="bg-white border hairline rounded-lg p-6 text-sm text-[#6B6A68] flex items-center gap-2"
             data-testid="voucher-review-loading">
          <Spinner size={14} className="animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-4">
          <div ref={refundRef}>
            <SafeRefundTool prefill={prefill} onChanged={refresh}
                            dashboardBase={alerts?.stripe_dashboard_base} />
          </div>

          <FinancialReviewQueue
            data={review} showResolved={showResolved}
            onToggleResolved={() => setShowResolved((v) => !v)}
            onChanged={refresh} onRefundCharge={refundCharge}
            dashboardBase={alerts?.stripe_dashboard_base}
          />

          <DisputePlaybook
            openDisputes={groupIncidents(
              (review?.items || []).filter((i) => i.status === "open" && i.band === "clock"),
            ).length}
            soonest={alerts?.soonest_evidence_due_by}
          />

          <ReplayQueue data={replays} onChanged={load} />
        </div>
      )}
    </section>
  );
}

// ======================================================== dispute playbook
function DisputePlaybook({ openDisputes, soonest }) {
  const [open, setOpen] = useState(openDisputes > 0);
  const dl = deadlineInfo(soonest);
  return (
    <div className="bg-white border hairline rounded-lg overflow-hidden" data-testid="dispute-playbook">
      <button
        type="button" onClick={() => setOpen((v) => !v)}
        data-testid="dispute-playbook-toggle"
        className="w-full px-4 py-3 flex items-center gap-3 text-left bg-[#F4F2FF] hover:bg-[#EEEBFF]"
      >
        {open ? <CaretDown size={12} weight="bold" className="text-[#4B45CC] shrink-0" />
              : <CaretRight size={12} weight="bold" className="text-[#4B45CC] shrink-0" />}
        <span className="min-w-0">
          <span className="block text-xs font-medium text-[#1C1B1A]">Dispute response routine</span>
          <span className="block text-[10px] text-[#6B6A68]">
            {openDisputes > 0
              ? `${openDisputes} dispute${openDisputes === 1 ? "" : "s"} running right now — the platform froze what it could, but only a human can answer Stripe.`
              : "What to do the moment a chargeback lands. Nothing here is automatic."}
          </span>
        </span>
        {dl && (
          <span className={`ml-auto text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border shrink-0 ${DEADLINE_STYLE[dl.tone]}`}
                data-testid="dispute-playbook-deadline">
            {dl.label}
          </span>
        )}
      </button>
      {open && (
        <div className="p-4 space-y-3 text-[11px] text-[#3A3936] leading-relaxed border-t hairline"
             data-testid="dispute-playbook-body">
          <ol className="list-decimal list-outside ml-4 space-y-2">
            <li>
              <strong>Check what was frozen.</strong> The entry above shows the held amount. A
              dispute with nothing held means that credit was already spent — expect a decision
              item when it closes instead.
            </li>
            <li>
              <strong>Read the reason</strong> (fraudulent, product_not_received, duplicate,
              subscription_canceled). It decides which evidence actually wins.
            </li>
            <li>
              <strong>Gather evidence.</strong> The Stripe receipt, what the credit was spent on
              (the usage rows on that silo prove delivery), the checkout email and IP, and the
              refund policy the buyer agreed to at checkout.
            </li>
            <li>
              <strong>Submit it in the Stripe Dashboard before the deadline.</strong>{" "}
              <span className="text-[#7A3A2A]">Nothing in this app submits evidence for you, and
              Stripe awards an unanswered dispute to the cardholder automatically.</span>
            </li>
            <li>
              <strong>Or end it: "Refund instead of fighting it"</strong> on the entry itself
              pre-fills the Safe refund box with that charge. If the complaint is obviously fair,
              refunding before the deadline usually costs less than losing — the dispute fee is
              not refundable either way.
            </li>
          </ol>
        </div>
      )}
    </div>
  );
}

// ======================================================== replay queue
function ReplayQueue({ data, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const items = data?.items || [];
  const run = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/vouchers/replays/${id}/run`);
      toast.success("Replayed. Idempotent — nothing moved twice.");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Replay failed again.");
    } finally { setBusyId(null); }
  };

  if (!items.length) {
    return (
      <div className="bg-white border hairline rounded-lg px-4 py-2.5 flex items-center gap-3"
           data-testid="voucher-replay-empty">
        <ArrowsClockwise size={14} weight="duotone" className="text-[#A9A7A2] shrink-0" />
        <span className="text-[11px] text-[#6B6A68]">Webhook replay queue</span>
        <span className="ml-auto text-[10px] text-[#A9A7A2] italic">
          nothing captured — any payment webhook that fails lands here with its payload
        </span>
      </div>
    );
  }

  return (
    <div className="bg-white border hairline rounded-lg overflow-hidden" data-testid="voucher-replay-queue">
      <div className="px-4 py-3 border-b hairline bg-[#F7F6F1] flex items-center gap-3">
        <ArrowsClockwise size={15} weight="duotone" className="text-[#6B6A68] shrink-0" />
        <div className="min-w-0">
          <div className="text-xs font-medium text-[#1C1B1A]">Webhook replay queue</div>
          <div className="text-[10px] text-[#6B6A68]">
            {data?.deferred_count
              ? `${data.deferred_count} operation(s) arrived BEFORE the purchase was fulfilled and apply automatically.`
              : `${data?.pending_count || 0} operation(s) failed inside a Stripe webhook and are waiting to be re-run.`}
          </div>
        </div>
      </div>
      <div className="divide-y hairline">
        {items.map((r) => (
          <div key={r.id} className="px-4 py-2.5 flex items-center gap-3 text-[11px]"
               data-testid={`voucher-replay-row-${r.id}`}>
            <span className="flex-1 min-w-0">
              <span className="block text-[#1C1B1A] truncate">
                {r.stage} · {r.event_type} · {r.error_category}
              </span>
              <span className="block text-[10px] text-[#6B6A68] truncate">
                {r.stripe_charge_id || r.dispute_id || r.stripe_event_id} · attempts {r.attempts || 0}
                {" · "}{new Date(r.created_at).toLocaleString()}
              </span>
            </span>
            <span className={`text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border shrink-0 ${
              r.status === "pending" ? "border-[#D9A97A] text-[#8A5A00] bg-[#FDF8EE]"
              : r.status === "deferred" ? "border-[#A8B6C9] text-[#3A5A7A] bg-[#EEF3F8]"
              : r.status === "resolved" ? "border-[#B7CBB9] text-[#2F6B3A] bg-[#F1F7F1]"
              : "border-[#E5BBA8] text-[#7A3A2A] bg-[#FDF1ED]"}`}>
              {r.status}
            </span>
            <button
              type="button" onClick={() => run(r.id)} disabled={busyId === r.id}
              data-testid={`voucher-replay-run-${r.id}`}
              className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded border border-[#A89360] text-[#7A5C20] hover:bg-[#FAF2DA] disabled:opacity-50 shrink-0"
            >
              {busyId === r.id ? "Running…" : r.status === "deferred" ? "Apply" : "Run"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
